import { useCallback, useEffect, useState } from "react";
import { Loader2, Wifi, WifiOff } from "lucide-react";

import { checkApiHealth } from "@/api";
import { cn } from "@/lib/utils";

type ConnectionStatus = "checking" | "online" | "offline";

export function ServerStatusIndicator({ className }: { className?: string }) {
  const [status, setStatus] = useState<ConnectionStatus>("checking");

  const refresh = useCallback(async () => {
    setStatus("checking");
    const result = await checkApiHealth();
    setStatus(result.ok ? "online" : "offline");
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const result = await checkApiHealth();
      if (!cancelled) setStatus(result.ok ? "online" : "offline");
    };
    run();
    const interval = window.setInterval(run, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const label =
    status === "checking"
      ? "Checking server…"
      : status === "online"
        ? "Server connected"
        : "Server offline";

  return (
    <button
      type="button"
      onClick={refresh}
      title={`${label}. Click to refresh.`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        status === "online" &&
          "border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-400",
        status === "offline" &&
          "border-destructive/40 bg-destructive/10 text-destructive",
        status === "checking" &&
          "border-muted-foreground/30 bg-muted text-muted-foreground",
        className,
      )}
    >
      {status === "checking" && (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      )}
      {status === "online" && <Wifi className="h-3.5 w-3.5" />}
      {status === "offline" && <WifiOff className="h-3.5 w-3.5" />}
      <span className="hidden sm:inline">
        {status === "online"
          ? "Connected"
          : status === "offline"
            ? "Offline"
            : "Checking…"}
      </span>
    </button>
  );
}
